import React, { useState } from 'react'

import { useNavigate, useLocation } from 'react-router-dom'

import * as Yup from 'yup'
import { Form, Formik, FormikHelpers } from 'formik'

import { Box, Stack, Typography, Button, Alert } from '@mui/material'
import { CheckCircleOutline } from '@mui/icons-material'

import { useMutationPutAuthPasswordReset } from '@/api/auth/mutations'

import { HttpError } from '@/api/http'

import { NewPasswordInput } from '@/components/Inputs/NewPassword'
import { PasswordInput } from '@/components/Inputs/Password'
import { validatePassword } from '@/components/Inputs/NewPassword/helpers/validatePassword'

import { useBackButton } from '@/hooks/useBackButton'

export interface NewPasswordFormData {
  password: string
  confirmPassword: string
}

const initialNewPasswordValues = {
  password: '',
  confirmPassword: '',
} as NewPasswordFormData

const newPasswordValidationSchema = Yup.object().shape({
  password: Yup.string().required('O campo de senha é obrigatório').test({
    name: 'password',
    test: validatePassword,
    message: '',
  }),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'As senhas devem ser iguais')
    .required('O campo de confirmação de senha é obrigatório'),
})

const AuthNewPasswordPage: React.FC = () => {
  useBackButton()

  const navigate = useNavigate()
  const location = useLocation()

  const [passwordChanged, setPasswordChanged] = useState(false)
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null)

  const { username, pin } = (location.state || {}) as {
    username: string
    pin: string
  }

  const { mutateAsync: mutatePutAuthPasswordReset } = useMutationPutAuthPasswordReset()

  const handleOnSubmitNewPassword = (
    values: NewPasswordFormData,
    { setSubmitting }: FormikHelpers<NewPasswordFormData>
  ) => {
    const { password } = values

    if (!username || !pin) {
      navigate('/auth/forgot-password', { state: location.state })
    }

    mutatePutAuthPasswordReset({ username, pin, password })
      .then(() => {
        setPasswordChanged(true)
      })
      .catch((error: HttpError) => {
        setNewPasswordError(error.message || 'Ocorreu um erro ao alterar a senha! Tente novamente mais tarde.')
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleOnClickLogin = () => {
    navigate('/auth/login', { state: location.state })
  }

  return (
    <Stack direction="column" alignItems="center" justifyContent="center" spacing={4}>
      <Stack direction="column" alignItems="center" textAlign="center">
        <Typography variant="h6" fontWeight="bold">
          Definir nova senha
        </Typography>
        <Typography variant="subtitle1">Carregue fácil, Ande mais!</Typography>
      </Stack>

      {passwordChanged ? (
        <Stack direction="column" alignItems="center" spacing={2}>
          <CheckCircleOutline sx={{ fontSize: 80, color: 'success.main' }} />
          <Typography
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            fontWeight="bold"
            textAlign="center"
            color="success"
          >
            Senha alterada com sucesso!
          </Typography>
          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} variant="subtitle1" textAlign="center">
            Sua senha foi alterada com sucesso. Você já pode fazer login.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ borderRadius: 20 }}
            onClick={handleOnClickLogin}
            disableElevation
          >
            Fazer login
          </Button>
        </Stack>
      ) : (
        <Formik
          initialValues={initialNewPasswordValues}
          validationSchema={newPasswordValidationSchema}
          onSubmit={handleOnSubmitNewPassword}
        >
          {({ submitForm, isSubmitting }) => (
            <Box component={Form} width="100%">
              <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
                {newPasswordError ? (
                  <Alert
                    severity="error"
                    sx={{
                      width: '100%',
                      alignItems: 'center',
                      borderRadius: 4,
                    }}
                  >
                    {newPasswordError}
                  </Alert>
                ) : null}

                <NewPasswordInput name="password" placeholder="Digite sua nova senha" autoComplete="new-password" />
                <PasswordInput
                  name="confirmPassword"
                  placeholder="Confirme sua nova senha"
                  autoComplete="new-password"
                />

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ borderRadius: 20 }}
                  onClick={submitForm}
                  loading={isSubmitting}
                  disableElevation
                >
                  Confirmar nova senha
                </Button>
              </Stack>
            </Box>
          )}
        </Formik>
      )}
    </Stack>
  )
}

export default AuthNewPasswordPage
